import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Car, Phone, Clock, AlertCircle, Truck, CheckSquare, Square, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { vehiclesAPI, handleApiError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import DeliveryDialog from '../components/DeliveryDialog';
import { useFlashMessage } from '../components/FlashMessage';

const Vehicles = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(['received', 'called']); // Multi-select: Teslim Alındı + Çağrıldı
  const [vehicles, setVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeliveryDialog, setShowDeliveryDialog] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const { FlashContainer } = useFlashMessage();
  const filterDropdownRef = useRef(null);

  // Dropdown dışına tıklama ile kapatma
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Owner ve Staff yetkisi kontrolü
  const hasAccess = user && (
    user.isStaff ||
    user.user_type === 'staff' ||
    user.isOwner ||
    user.user_type === 'owner'
  );

  useEffect(() => {
    if (hasAccess) {
      loadVehicles();
    }
  }, [statusFilter, hasAccess]);

  if (!hasAccess) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
        <p className="text-gray-500 text-sm">Kullanıcı türü: {user?.user_type}</p>
      </div>
    );
  }

  const loadVehicles = async () => {
    setIsLoading(true);
    setError('');

    try {
      if (user?.locations && user.locations.length > 0) {
        const allVehicles = [];

        // Her lokasyon için araçları getir (aktif araçlar)
        for (const location of user.locations) {
          try {
            const vehiclesResponse = await vehiclesAPI.getActiveVehicles(location.id);
            if (vehiclesResponse.data.success) {
              allVehicles.push(...vehiclesResponse.data.data);
            }
          } catch (error) {
            console.warn(`Failed to load vehicles for location ${location.id}:`, error);
          }
        }

        // 'delivered' seçiliyse teslim edilmiş araçları da getir
        if (statusFilter.includes('delivered')) {
          for (const location of user.locations) {
            try {
              // Son 24 saat içinde teslim edilmiş araçları getir
              const deliveredResponse = await vehiclesAPI.getDeliveredVehicles(location.id);
              if (deliveredResponse.data.success) {
                allVehicles.push(...deliveredResponse.data.data);
              }
            } catch (error) {
              console.warn(`Failed to load delivered vehicles for location ${location.id}:`, error);
            }
          }
        }

        setVehicles(allVehicles);
      }
    } catch (err) {
      console.error('Failed to load vehicles:', err);
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'received': return 'Teslim Alındı';
      case 'washing': return 'Yıkanıyor';
      case 'parked': return 'Park Edildi';
      case 'ready': return 'Hazır';
      case 'called': return 'Çağrıldı';
      case 'delivered': return 'Teslim Edildi';
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'received': return 'bg-blue-100 text-blue-800';
      case 'washing': return 'bg-yellow-100 text-yellow-800';
      case 'parked': return 'bg-purple-100 text-purple-800';
      case 'ready': return 'bg-green-100 text-green-800';
      case 'called': return 'bg-orange-100 text-orange-800';
      case 'delivered': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getServiceTypeText = (type) => {
    switch (type) {
      case 'both': return 'Vale + Yıkama';
      case 'wash': return 'Oto Yıkama';
      case 'valet': return 'Vale';
      default: return type;
    }
  };

  // Arama ve filtreleme
  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = searchQuery === '' ||
      vehicle.plate_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vehicle.customer_name?.toLowerCase().includes(searchQuery.toLowerCase());

    // Multi-select: Seçili statuslardan herhangi biri ile eşleşsin
    const matchesStatus = statusFilter.length === 0 || statusFilter.includes(vehicle.status);

    return matchesSearch && matchesStatus;
  });

  const handleDeliverySuccess = () => {
    loadVehicles(); // Araç listesini yenile
  };

  const toggleStatusFilter = (status) => {
    if (statusFilter.includes(status)) {
      // Eğer zaten seçiliyse, çıkar
      setStatusFilter(statusFilter.filter(s => s !== status));
    } else {
      // Seçili değilse, ekle
      setStatusFilter([...statusFilter, status]);
    }
  };

  const handleVehicleClick = (vehicle) => {
    // Owner ise sadece okuyabilir (read-only), Staff ise düzenleyebilir
    if (user.isOwner || user.user_type === 'owner') {
      navigate(`/owner/vehicles/details/${vehicle.id}`);
    } else {
      navigate(`/vehicles/status/${vehicle.id}`);
    }
  };

  // Kullanıcının lokasyonlarına göre mevcut servis türlerini al
  const getAvailableServices = () => {
    if (!user?.locations) return new Set();

    const services = new Set();
    user.locations.forEach(location => {
      if (location.type === 'wash' || location.type === 'both') {
        services.add('washing');
      }
      if (location.type === 'valet' || location.type === 'both') {
        services.add('parked');
        services.add('called');
      }
    });
    return services;
  };

  const availableServices = getAvailableServices();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header - Desktop Only - Sadece Staff için */}
      {!user?.isOwner && (
        <div className="hidden lg:flex flex-row gap-3 justify-end">
          <button
            onClick={() => setShowDeliveryDialog(true)}
            className="btn-secondary flex items-center justify-center"
          >
            <Truck className="w-4 h-4 mr-2" />
            Araç Teslimi
          </button>
          <button
            onClick={() => navigate('/vehicles/new')}
            className="btn-primary flex items-center justify-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Araç Girişi
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Plaka numarası, müşteri adı ile arayın..."
              className="form-input pl-10 hidden sm:block"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <input
              type="text"
              placeholder="Arama..."
              className="form-input pl-10 sm:hidden"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Multi-Select Status Filter */}
        <div className="relative" ref={filterDropdownRef}>
          <button
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            className="w-full sm:w-64 flex items-center justify-between px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#7a2c2c] focus:border-transparent transition-colors"
          >
            <span className="flex items-center">
              <Filter className="w-4 h-4 mr-2" />
              {statusFilter.length === 0 ? 'Tüm Durumlar' : `${statusFilter.length} Durum Seçili`}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilterDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showFilterDropdown && (
            <div className="absolute z-10 mt-1 w-full sm:w-64 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-y-auto">
              <div className="p-2 space-y-1">
                {/* Teslim Alındı */}
                <label className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={statusFilter.includes('received')}
                    onChange={() => toggleStatusFilter('received')}
                    className="sr-only"
                  />
                  {statusFilter.includes('received') ? (
                    <CheckSquare className="w-5 h-5 text-[#7a2c2c] mr-3" />
                  ) : (
                    <Square className="w-5 h-5 text-gray-400 mr-3" />
                  )}
                  <span className="text-sm text-gray-700">Teslim Alındı</span>
                </label>

                {/* Yıkanıyor */}
                {availableServices.has('washing') && (
                  <label className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={statusFilter.includes('washing')}
                      onChange={() => toggleStatusFilter('washing')}
                      className="sr-only"
                    />
                    {statusFilter.includes('washing') ? (
                      <CheckSquare className="w-5 h-5 text-[#7a2c2c] mr-3" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400 mr-3" />
                    )}
                    <span className="text-sm text-gray-700">Yıkanıyor</span>
                  </label>
                )}

                {/* Park Edildi */}
                {availableServices.has('parked') && (
                  <label className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={statusFilter.includes('parked')}
                      onChange={() => toggleStatusFilter('parked')}
                      className="sr-only"
                    />
                    {statusFilter.includes('parked') ? (
                      <CheckSquare className="w-5 h-5 text-[#7a2c2c] mr-3" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400 mr-3" />
                    )}
                    <span className="text-sm text-gray-700">Park Edildi</span>
                  </label>
                )}

                {/* Hazır */}
                <label className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={statusFilter.includes('ready')}
                    onChange={() => toggleStatusFilter('ready')}
                    className="sr-only"
                  />
                  {statusFilter.includes('ready') ? (
                    <CheckSquare className="w-5 h-5 text-[#7a2c2c] mr-3" />
                  ) : (
                    <Square className="w-5 h-5 text-gray-400 mr-3" />
                  )}
                  <span className="text-sm text-gray-700">Hazır</span>
                </label>

                {/* Çağrıldı */}
                {availableServices.has('called') && (
                  <label className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={statusFilter.includes('called')}
                      onChange={() => toggleStatusFilter('called')}
                      className="sr-only"
                    />
                    {statusFilter.includes('called') ? (
                      <CheckSquare className="w-5 h-5 text-[#7a2c2c] mr-3" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400 mr-3" />
                    )}
                    <span className="text-sm text-gray-700">Çağrıldı</span>
                  </label>
                )}

                {/* Teslim Edildi */}
                <label className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={statusFilter.includes('delivered')}
                    onChange={() => toggleStatusFilter('delivered')}
                    className="sr-only"
                  />
                  {statusFilter.includes('delivered') ? (
                    <CheckSquare className="w-5 h-5 text-[#7a2c2c] mr-3" />
                  ) : (
                    <Square className="w-5 h-5 text-gray-400 mr-3" />
                  )}
                  <span className="text-sm text-gray-700">Teslim Edildi</span>
                </label>
              </div>

              {/* Clear All / Select All */}
              <div className="border-t border-gray-200 p-2 flex gap-2">
                <button
                  onClick={() => setStatusFilter([])}
                  className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 rounded"
                >
                  Temizle
                </button>
                <button
                  onClick={() => setStatusFilter(['received', 'washing', 'parked', 'ready', 'called', 'delivered'])}
                  className="flex-1 px-3 py-2 text-xs font-medium text-[#7a2c2c] hover:bg-gray-50 rounded"
                >
                  Tümünü Seç
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Vehicle list */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            Aktif Araçlar ({filteredVehicles.length})
          </h3>
        </div>
        <div className="card-body">
          {filteredVehicles.length > 0 ? (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: '8%' }} /> {/* Referans */}
                      <col style={{ width: '15%' }} /> {/* Araç - Primary */}
                      <col style={{ width: '15%' }} /> {/* Müşteri */}
                      <col style={{ width: '10%' }} /> {/* Lokasyon */}
                      <col style={{ width: '10%' }} /> {/* Hizmet */}
                      <col style={{ width: '10%' }} /> {/* Durum */}
                      <col style={{ width: '8%' }} /> {/* Park Yeri */}
                      <col style={{ width: '8%' }} /> {/* Tutar */}
                      <col style={{ width: '8%' }} /> {/* Giriş Saati */}
                      <col style={{ width: '8%' }} /> {/* Çıkış Saati */}
                    </colgroup>
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Referans
                        </th>
                        <th scope="col" className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Araç
                        </th>
                        <th scope="col" className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Müşteri
                        </th>
                        <th scope="col" className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Lokasyon
                        </th>
                        <th scope="col" className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Hizmet
                        </th>
                        <th scope="col" className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Durum
                        </th>
                        <th scope="col" className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Park Yeri
                        </th>
                        <th scope="col" className="px-5 py-3.5 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Tutar
                        </th>
                        <th scope="col" className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Giriş
                        </th>
                        <th scope="col" className="px-6 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Çıkış
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {filteredVehicles.map((vehicle) => (
                        <tr key={vehicle.id} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleVehicleClick(vehicle)}
                              className="text-sm font-medium text-[#7a2c2c] hover:text-[#5a1f1f] cursor-pointer"
                            >
                              {vehicle.reference_code ? (
                                <span className="text-lg font-bold">{vehicle.reference_code}</span>
                              ) : (
                                <span className="text-sm">#{vehicle.id}</span>
                              )}
                            </button>
                            <div className="text-xs text-gray-500">{vehicle.reference_code ? 'Referans' : 'ID'}</div>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-red-100 to-red-50 rounded-lg flex items-center justify-center border border-red-200">
                                <Car className="h-5 w-5 text-[#7a2c2c]" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{vehicle.plate_number}</div>
                                <div className="text-sm text-gray-500">{vehicle.brand} {vehicle.model}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            {vehicle.customer_name && vehicle.customer_name !== 'Veribil' ? (
                              <div className="flex flex-col">
                                <div className="text-sm font-medium text-gray-900">{vehicle.customer_name}</div>
                                {vehicle.customer_phone && vehicle.customer_phone !== '00000000000' && (
                                  <div className="text-xs text-gray-500 flex items-center mt-0.5">
                                    <Phone className="h-3 w-3 mr-1" />
                                    {vehicle.customer_phone}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-400">-</div>
                            )}
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{vehicle.location_name || '-'}</div>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-900">
                            {getServiceTypeText(vehicle.service_type)}
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(vehicle.status)}`}>
                              {getStatusText(vehicle.status)}
                            </span>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-900">
                            {vehicle.parking_spot || '-'}
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap text-right">
                            <span className="text-sm font-semibold text-gray-900 tabular-nums">
                              ₺{parseFloat(vehicle.actual_amount || 0).toFixed(2)}
                            </span>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <div className="flex items-center text-xs text-gray-500">
                              <Clock className="h-3 w-3 mr-1" />
                              {new Date(vehicle.check_in).toLocaleString('tr-TR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {vehicle.check_out ? (
                              <div className="flex items-center text-xs text-gray-500">
                                <Clock className="h-3 w-3 mr-1" />
                                {new Date(vehicle.check_out).toLocaleString('tr-TR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4">
                {filteredVehicles.map((vehicle) => (
                  <div key={vehicle.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <Car className="h-4 w-4 text-gray-600" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{vehicle.plate_number}</div>
                          <div className="text-xs text-gray-500">{vehicle.brand} {vehicle.model}</div>
                          <button
                            onClick={() => handleVehicleClick(vehicle)}
                            className="text-xs text-[#7a2c2c] hover:text-[#5a1f1f] cursor-pointer"
                          >
                            {vehicle.reference_code ? (
                              <span className="text-base font-bold">Ref: {vehicle.reference_code}</span>
                            ) : (
                              <span>ID: #{vehicle.id}</span>
                            )}
                          </button>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(vehicle.status)}`}>
                        {getStatusText(vehicle.status)}
                      </span>
                    </div>

                    <div className="space-y-2 text-sm">
                      {vehicle.customer_name && vehicle.customer_name !== 'Veribil' && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Müşteri:</span>
                          <span className="text-gray-900">{vehicle.customer_name}</span>
                        </div>
                      )}
                      {vehicle.customer_phone && vehicle.customer_phone !== '00000000000' && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Telefon:</span>
                          <span className="text-gray-900">{vehicle.customer_phone}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Lokasyon:</span>
                        <span className="text-gray-900">{vehicle.location_name || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Hizmet:</span>
                        <span className="text-gray-900">{getServiceTypeText(vehicle.service_type)}</span>
                      </div>
                      {vehicle.parking_spot && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Park Yeri:</span>
                          <span className="text-gray-900">{vehicle.parking_spot}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Tutar:</span>
                        <span className="text-gray-900 font-medium">₺{parseFloat(vehicle.actual_amount || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Giriş:</span>
                        <span className="text-gray-900 text-xs">{new Date(vehicle.check_in).toLocaleString('tr-TR')}</span>
                      </div>
                      {vehicle.check_out && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Çıkış:</span>
                          <span className="text-gray-900 text-xs">{new Date(vehicle.check_out).toLocaleString('tr-TR')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Car className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchQuery || statusFilter.length > 0 ? 'Araç bulunamadı' : 'Henüz araç bulunmuyor'}
              </h3>
              <p className="text-gray-600">
                {searchQuery || statusFilter.length > 0
                  ? 'Arama kriterlerinize uygun araç bulunmuyor'
                  : 'İlk araç girişi için "Araç Girişi" butonuna tıklayın'
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Delivery Dialog */}
      <DeliveryDialog
        isOpen={showDeliveryDialog}
        onClose={() => setShowDeliveryDialog(false)}
        onDeliverySuccess={handleDeliverySuccess}
      />


      {/* Flash Messages */}
      <FlashContainer />

    </div>
  );
};

export default Vehicles;